import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Sparkles, User, ChevronDown, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios, { API } from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme, SKIN_TONES } from "@/context/ThemeContext";
import Footer from "@/components/layout/Footer";
import { usePresenceTracking } from '@/hooks/usePresenceTracking';
import { validateName, formatName } from '@/utils/nameValidation';
import { 
  trackOnboardingStart, 
  trackOnboardingStep, 
  trackOnboardingComplete,
  trackSignup 
} from '@/utils/fbPixel';

// Default referral source options (fallback if API fails)
const DEFAULT_REFERRAL_OPTIONS = [
  "Audria Richmond®",
  "UnCloned® Free Community",
  "UnCloned® Coaching Client",
  "UnCloned® Consulting Client",
  "Instagram",
  "Facebook",
  "LinkedIn",
  "YouTube",
  "TikTok",
  "Google Search",
  "Email Newsletter",
  "Podcast",
  "Live Event or Conference",
  "Webinar or Challenge",
  "Friend or Referral",
  "Facebook Ad",
  "Instagram Ad",
  "Other"
];

const OnboardingPage = () => {
  const { user, setUser } = useAuth();
  const { updateSkinTone } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Track presence during onboarding
  usePresenceTracking('onboarding', user?.user_id);
  
  // Check if user is from a partnership (skip referral step)
  const isPartnershipUser = !!(user?.partnership_id || user?.partnership_name);
  
  const [step, setStep] = useState(1);
  const [referralOptions, setReferralOptions] = useState(DEFAULT_REFERRAL_OPTIONS);
  const [formData, setFormData] = useState({
    first_name: user?.name?.split(' ')[0] || user?.first_name || '',
    last_name: user?.name?.split(' ').slice(1).join(' ') || user?.last_name || '',
    phone_number: '',
    emoji_skin_tone: 'default',
    // Pre-fill referral for partnership users
    referral_source: isPartnershipUser ? `Partnership: ${user?.partnership_name || 'Organization'}` : '',
    referral_other: '',
  });
  const [saving, setSaving] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    firstName: null,
    lastName: null
  });
  
  // Track onboarding start on mount (only once)
  const hasTrackedStart = useRef(false);
  useEffect(() => {
    if (!hasTrackedStart.current) {
      trackOnboardingStart();
      trackOnboardingStep({ step: 1, step_name: 'Name Entry' });
      hasTrackedStart.current = true;
      console.log('[FB Pixel] Onboarding started - Step 1');
    }
  }, []);
  
  // Track step changes
  useEffect(() => {
    if (hasTrackedStart.current && step > 1) {
      trackOnboardingStep({ step });
      console.log(`[FB Pixel] Onboarding step ${step}`);
      
      // Track completion when reaching review step
      if (step === 5) {
        trackOnboardingComplete();
        console.log('[FB Pixel] Onboarding review reached');
      }
    }
  }, [step]);

  // Fetch referral options from API
  useEffect(() => {
    const fetchReferralOptions = async () => {
      try {
        const res = await axios.get(`${API}/settings/referral-options`);
        if (res.data?.options && res.data.options.length > 0) {
          setReferralOptions(res.data.options);
        }
      } catch (err) {
        console.log('Using default referral options');
      }
    };
    fetchReferralOptions();
  }, []);

  // Get user from location state if available (from AuthCallback)
  const currentUser = user || location.state?.user;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  const handleSkinToneSelect = (toneId) => {
    setFormData({ ...formData, emoji_skin_tone: toneId });
    updateSkinTone(toneId); // Live preview
  };

  const handleNext = () => {
    if (step === 1) {
      // Validate names
      const firstResult = validateName(formData.first_name, 'First name');
      const lastResult = validateName(formData.last_name, 'Last name');
      
      setFieldErrors({
        firstName: firstResult.error,
        lastName: lastResult.error
      });
      
      if (!firstResult.valid) {
        toast.error(firstResult.error);
        return;
      }
      if (!lastResult.valid) {
        toast.error(lastResult.error);
        return;
      }
      
      // Format names properly
      setFormData(prev => ({
        ...prev,
        first_name: formatName(prev.first_name),
        last_name: formatName(prev.last_name)
      }));
      
      setStep(2);
    } else if (step === 2) {
      setStep(3);
    } else if (step === 3) {
      // Partnership users skip the referral step (step 4)
      if (isPartnershipUser) {
        setStep(5); // Go directly to review
      } else {
        setStep(4);
      }
    } else if (step === 4) {
      if (!formData.referral_source) {
        toast.error("Please tell us how you heard about us");
        return;
      }
      if (formData.referral_source === "Other" && !formData.referral_other.trim()) {
        toast.error("Please specify how you heard about us");
        return;
      }
      setStep(5);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    
    // Note: Authentication is handled via HttpOnly cookies (set by login/redemption)
    // We don't check localStorage session_token here because:
    // 1. Partnership redemption uses HttpOnly cookies only
    // 2. The actual API call will return 401 if not authenticated
    // Let the API validate the session rather than preemptively checking localStorage
    
    try {
      const response = await axios.post(`${API}/auth/onboarding`, {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        phone_number: formData.phone_number.trim() || null,
        emoji_skin_tone: formData.emoji_skin_tone,
        referral_source: formData.referral_source,
        referral_other: formData.referral_source === "Other" ? formData.referral_other.trim() : null,
      });
      
      console.log('[Onboarding] Success! Response:', response.data);
      
      // Track successful registration with Facebook Pixel
      trackSignup({ 
        method: 'google',
        source: formData.referral_source === 'Other' ? formData.referral_other : formData.referral_source
      });
      console.log('[FB Pixel] CompleteRegistration event fired');
      
      // Update user state with completed onboarding
      const updatedUser = { ...response.data, onboarding_complete: true };
      setUser(updatedUser);
      
      // Store in localStorage to ensure persistence across navigation
      localStorage.setItem('uncloned_user', JSON.stringify(updatedUser));
      
      toast.success(`Welcome, ${formData.first_name}! ✨`);
      
      // Use window.location for a clean navigation that picks up the new state
      window.location.href = "/dashboard";
    } catch (error) {
      console.error("[Onboarding] Error:", error.response?.status, error.response?.data);
      
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
        window.location.href = '/login';
      } else {
        toast.error(error.response?.data?.detail || "Failed to save. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  // Get sample emoji with current skin tone
  const getSampleEmoji = (baseEmoji) => {
    const tone = SKIN_TONES.find(t => t.id === formData.emoji_skin_tone);
    if (!tone || tone.id === 'default') return baseEmoji;
    return baseEmoji + tone.modifier;
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-[var(--theme-background)] to-[var(--theme-background-secondary)] p-4">
      <div className="flex-1 flex flex-col items-center justify-center pt-16 md:pt-20 lg:pt-8">
        <div className="text-center max-w-md w-full">
          <div className="flex items-center justify-center gap-4 mb-8">
            <Sparkles className="w-12 h-12 text-[var(--theme-primary)] float-animation" />
            <h1 className="text-4xl lg:text-5xl font-black gradient-text">
              Welcome!
            </h1>
            <Sparkles className="w-12 h-12 text-[var(--theme-primary)] float-animation" />
          </div>
          
          {/* Progress dots - Partnership users see 4 dots (skip referral step) */}
          <div className="flex justify-center gap-2 mb-8">
            {(isPartnershipUser ? [1, 2, 3, 5] : [1, 2, 3, 4, 5]).map((s, index) => (
              <div
                key={s}
                className={`w-3 h-3 rounded-full transition-all ${
                  s === step ? "bg-[var(--theme-primary)] scale-125" : s < step ? "bg-[var(--theme-secondary)]" : "bg-[var(--theme-background-secondary)]"
                }`}
              />
            ))}
          </div>

          {step === 1 && (
            <div className="glass-card p-8 fade-up">
              <h2 className="text-2xl font-bold mb-4 text-center" style={{ color: 'var(--theme-text)' }}>
                What's Your Name?
              </h2>
              <p className="mb-6 text-sm text-center" style={{ color: 'var(--theme-text-secondary)' }}>
                Let us know who you are ✨
              </p>
              
              <div className="space-y-4">
                <div>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--theme-primary)]" />
                    <Input
                      name="first_name"
                      value={formData.first_name}
                      onChange={(e) => {
                        handleChange(e);
                        // Real-time validation
                        if (e.target.value.length > 0) {
                          const result = validateName(e.target.value, 'First name');
                          setFieldErrors(prev => ({ ...prev, firstName: result.error }));
                        } else {
                          setFieldErrors(prev => ({ ...prev, firstName: null }));
                        }
                      }}
                      placeholder="Your first name"
                      className={`diary-input text-lg p-4 pl-10 ${fieldErrors.firstName ? 'border-red-500' : ''}`}
                      data-testid="first-name-input"
                      maxLength={50}
                    />
                  </div>
                  {fieldErrors.firstName && (
                    <p className="mt-1 text-xs text-red-400 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      {fieldErrors.firstName}
                    </p>
                  )}
                </div>
                
                <div>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--theme-secondary)]" />
                    <Input
                      name="last_name"
                      value={formData.last_name}
                      onChange={(e) => {
                        handleChange(e);
                        // Real-time validation
                        if (e.target.value.length > 0) {
                          const result = validateName(e.target.value, 'Last name');
                          setFieldErrors(prev => ({ ...prev, lastName: result.error }));
                        } else {
                          setFieldErrors(prev => ({ ...prev, lastName: null }));
                        }
                      }}
                      placeholder="Your last name"
                      className={`diary-input text-lg p-4 pl-10 ${fieldErrors.lastName ? 'border-red-500' : ''}`}
                      data-testid="last-name-input"
                      maxLength={50}
                    />
                  </div>
                  {fieldErrors.lastName && (
                    <p className="mt-1 text-xs text-red-400 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      {fieldErrors.lastName}
                    </p>
                  )}
                </div>
              </div>
              
              <Button
                onClick={handleNext}
                className="btn-primary w-full flex items-center justify-center gap-3 text-lg mt-6"
                data-testid="next-btn"
              >
                <Sparkles className="w-5 h-5" />
                Continue
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="glass-card p-8 fade-up">
              <h2 className="text-2xl font-bold mb-4 text-center" style={{ color: 'var(--theme-text)' }}>
                Pick Your Emoji Style {getSampleEmoji("👋")}
              </h2>
              <p className="mb-6 text-sm text-center" style={{ color: 'var(--theme-text-secondary)' }}>
                Choose your preferred emoji skin tone
              </p>
              
              <div className="flex flex-wrap justify-center gap-3 mb-6">
                {SKIN_TONES.map((tone) => (
                  <button
                    key={tone.id}
                    onClick={() => handleSkinToneSelect(tone.id)}
                    className={`flex flex-col items-center p-3 rounded-xl transition-all ${
                      formData.emoji_skin_tone === tone.id 
                        ? 'bg-[var(--theme-primary)]/30 ring-2 ring-[var(--theme-primary)] scale-110' 
                        : 'bg-[var(--theme-background-secondary)]/50 hover:bg-[var(--theme-background-secondary)] hover:scale-105'
                    }`}
                    data-testid={`skin-tone-${tone.id}`}
                  >
                    <span className="text-3xl mb-1">{tone.sample}</span>
                    <span className="text-xs" style={{ color: 'var(--theme-text-secondary)' }}>{tone.name}</span>
                  </button>
                ))}
              </div>
              
              {/* Preview with multiple emojis */}
              <div className="bg-[var(--theme-background-secondary)]/30 rounded-lg p-4 mb-6">
                <p className="text-xs mb-2" style={{ color: 'var(--theme-text-secondary)' }}>Preview</p>
                <div className="flex justify-center gap-3 text-3xl">
                  {getSampleEmoji("👍")} {getSampleEmoji("🤞")} {getSampleEmoji("🙏")} {getSampleEmoji("🤘")} {getSampleEmoji("👏")}
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={() => setStep(1)}
                  className="btn-secondary flex-1"
                >
                  Back
                </Button>
                <Button
                  onClick={handleNext}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                  data-testid="next-btn-2"
                >
                  <Sparkles className="w-5 h-5" />
                  Continue
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="glass-card p-8 fade-up">
              <h2 className="text-2xl font-bold mb-4 text-center" style={{ color: 'var(--theme-text)' }}>
                Contact Information
              </h2>
              <p className="mb-6 text-sm text-center" style={{ color: 'var(--theme-text-secondary)' }}>
                How can we reach you?
              </p>
              
              <div className="space-y-4">
                {/* Email from Google - read only */}
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--theme-primary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <Input
                    value={currentUser?.email || ''}
                    readOnly
                    className="diary-input text-lg p-4 pl-10 opacity-70 cursor-not-allowed"
                    data-testid="email-input"
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-green-400 font-medium">
                    ✓ from Google
                  </span>
                </div>
                
                {/* Phone number */}
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--theme-secondary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <Input
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleChange}
                    placeholder="Phone Number"
                    type="tel"
                    className="diary-input text-lg p-4 pl-10"
                    data-testid="phone-input"
                    maxLength={20}
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <Button
                  onClick={() => setStep(2)}
                  className="btn-secondary flex-1"
                >
                  Back
                </Button>
                <Button
                  onClick={handleNext}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                  data-testid="next-btn-3"
                >
                  <Sparkles className="w-5 h-5" />
                  Continue
                </Button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="glass-card p-8 fade-up">
              <h2 className="text-2xl font-bold mb-4 text-center" style={{ color: 'var(--theme-text)' }}>
                How Did You Hear About Us?
              </h2>
              <p className="mb-6 text-sm text-center" style={{ color: 'var(--theme-text-secondary)' }}>
                We'd love to know how you found us!
              </p>
              
              {/* Custom Dropdown */}
              <div className="relative mb-4">
                <button
                  type="button"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full p-4 rounded-xl text-left flex items-center justify-between transition-all"
                  style={{
                    background: 'var(--theme-background-secondary)',
                    border: '2px solid var(--theme-border)',
                    color: formData.referral_source ? 'var(--theme-text)' : 'var(--theme-text-secondary)',
                  }}
                  data-testid="referral-dropdown"
                >
                  <span>{formData.referral_source || "Select an option..."}</span>
                  <ChevronDown className={`w-5 h-5 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} style={{ color: 'var(--theme-primary)' }} />
                </button>
                
                {dropdownOpen && (
                  <div 
                    className="absolute z-50 w-full mt-2 rounded-xl overflow-hidden shadow-2xl max-h-64 overflow-y-auto"
                    style={{ 
                      background: 'var(--theme-background)',
                      border: '2px solid var(--theme-border)',
                    }}
                  >
                    {referralOptions.map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, referral_source: option });
                          setDropdownOpen(false);
                        }}
                        className="w-full p-3 text-left transition-all hover:bg-[var(--theme-primary)]/20"
                        style={{ 
                          color: 'var(--theme-text)',
                          background: formData.referral_source === option ? 'var(--theme-primary)/30' : 'transparent',
                        }}
                        data-testid={`referral-option-${option.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                      >
                        {formData.referral_source === option && <span className="mr-2">✓</span>}
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* "Other" text input */}
              {formData.referral_source === "Other" && (
                <div className="mb-4 fade-up">
                  <Input
                    name="referral_other"
                    value={formData.referral_other}
                    onChange={handleChange}
                    placeholder="Please specify..."
                    className="diary-input text-lg p-4"
                    data-testid="referral-other-input"
                    maxLength={100}
                  />
                </div>
              )}
              
              <div className="flex gap-3 mt-6">
                <Button
                  onClick={() => setStep(3)}
                  className="btn-secondary flex-1"
                >
                  Back
                </Button>
                <Button
                  onClick={handleNext}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                  data-testid="next-btn-4"
                >
                  <Sparkles className="w-5 h-5" />
                  Continue
                </Button>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="glass-card p-8 fade-up">
              <h2 className="text-2xl font-bold mb-4 text-center" style={{ color: 'var(--theme-text)' }}>
                You're All Set! 🎉
              </h2>
              <p className="mb-6 text-sm text-center" style={{ color: 'var(--theme-text-secondary)' }}>
                Your journal is ready, {formData.first_name}! Start capturing your thoughts and feelings.
              </p>
              
              {/* Summary */}
              <div className="bg-[var(--theme-background-secondary)]/30 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm mb-2" style={{ color: 'var(--theme-text-secondary)' }}>
                  <span className="text-[var(--theme-primary)]">Name:</span> {formData.first_name} {formData.last_name}
                </p>
                <p className="text-sm mb-2" style={{ color: 'var(--theme-text-secondary)' }}>
                  <span className="text-[var(--theme-primary)]">Email:</span> {currentUser?.email}
                </p>
                <p className="text-sm mb-2" style={{ color: 'var(--theme-text-secondary)' }}>
                  <span className="text-[var(--theme-primary)]">Emoji Style:</span> {getSampleEmoji("👋")} {SKIN_TONES.find(t => t.id === formData.emoji_skin_tone)?.name}
                </p>
                {formData.phone_number && (
                  <p className="text-sm mb-2" style={{ color: 'var(--theme-text-secondary)' }}>
                    <span className="text-[var(--theme-primary)]">Phone:</span> {formData.phone_number}
                  </p>
                )}
                {isPartnershipUser ? (
                  <p className="text-sm" style={{ color: 'var(--theme-text-secondary)' }}>
                    <span className="text-[var(--theme-primary)]">Organization:</span> {user?.partnership_name}
                  </p>
                ) : (
                  <p className="text-sm" style={{ color: 'var(--theme-text-secondary)' }}>
                    <span className="text-[var(--theme-primary)]">Found us via:</span> {formData.referral_source === "Other" ? formData.referral_other : formData.referral_source}
                  </p>
                )}
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={() => setStep(isPartnershipUser ? 3 : 4)}
                  className="btn-secondary flex-1"
                >
                  Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={saving}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                  data-testid="finish-btn"
                >
                  {saving ? (
                    <>
                      <div className="loading-spinner w-5 h-5" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Let's Go!
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          <p className="mt-6 text-sm" style={{ color: 'var(--theme-text-secondary)' }}>
            Your information is private and secure.
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default OnboardingPage;
